import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import {
  fetchBunchSizes,
  createBunchSize,
  updateBunchSize,
  deleteBunchSize,
} from "../api/bunchSizeApi";
import "../styles/Simplemanage.css";

const ManageBunchSizes = () => {
  const [bunchSizes, setBunchSizes] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState({ stems_count: "" });

  // ── Load ──
  const loadBunchSizes = async () => {
    try {
      const data = await fetchBunchSizes();
      setBunchSizes(data);
    } catch {
      Swal.fire("Error", "Failed to load bunch sizes", "error");
    }
  };

  useEffect(() => { loadBunchSizes(); }, []);

  // ── Form ──
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const resetForm = () => {
    setEditingItem(null);
    setForm({ stems_count: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const count = Number(form.stems_count);
    if (!count || count < 1) return Swal.fire("Required", "Enter a valid stems count", "error");
    try {
      if (editingItem) {
        await updateBunchSize(editingItem.id, { stems_count: count });
        Swal.fire("Updated!", "Bunch size updated successfully", "success");
      } else {
        await createBunchSize({ stems_count: count });
        Swal.fire("Created!", "Bunch size added successfully", "success");
      }
      loadBunchSizes();
      resetForm();
    } catch {
      Swal.fire("Error", "Operation failed", "error");
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setForm({ stems_count: item.stems_count });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Delete this bunch size?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#a92f6f",
      cancelButtonColor: "#a8a49a",
      confirmButtonText: "Yes, delete it",
    });
    if (!confirm.isConfirmed) return;
    try {
      await deleteBunchSize(id);
      loadBunchSizes();
      Swal.fire("Deleted!", "Record removed successfully", "success");
    } catch {
      Swal.fire("Error", "Failed to delete record", "error");
    }
  };

  return (
    <div className="sm-root">

      {/* ── Header ── */}
      <div className="sm-header">
        <h1 className="sm-header-title">
          Manage <span>Bunch Sizes</span>
        </h1>
        <span className="sm-badge">
          {bunchSizes.length} {bunchSizes.length === 1 ? "Size" : "Sizes"}
        </span>
      </div>

      <div className="sm-layout">

        {/* ── Form Card ── */}
        <div className="sm-card">
          <div className="sm-card-header">
            <div className="sm-card-icon sm-card-icon--rose">💐</div>
            <div>
              <p className="sm-card-title">
                {editingItem ? "Edit Bunch Size" : "New Bunch Size"}
              </p>
              <p className="sm-card-sub">
                {editingItem
                  ? `Editing: ${editingItem.stems_count} stems`
                  : "Define a new bunch stem count"}
              </p>
            </div>
          </div>

          <div className="sm-card-body">
            {editingItem && (
              <div className="sm-edit-banner" style={{ marginBottom: 16 }}>
                <span>✏️</span>
                <span>
                  Editing bunch of <strong>{editingItem.stems_count} stems</strong>
                </span>
              </div>
            )}

            <form className="sm-form" onSubmit={handleSubmit}>
              <div className="sm-field">
                <label className="sm-label">
                  Stems Count <span className="sm-req">*</span>
                </label>
                <input
                  className="sm-input"
                  name="stems_count"
                  type="number"
                  min="1"
                  placeholder="e.g. 10, 20, 50"
                  value={form.stems_count}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="sm-btn-row">
                <button type="submit" className="sm-btn sm-btn--primary">
                  {editingItem ? "✓ Update Size" : "+ Add Size"}
                </button>
                {editingItem && (
                  <button type="button" className="sm-btn sm-btn--ghost" onClick={resetForm}>
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>

        {/* ── Table Card ── */}
        <div className="sm-card">
          <div className="sm-card-header">
            <div className="sm-card-icon sm-card-icon--olive">📋</div>
            <div>
              <p className="sm-card-title">All Bunch Sizes</p>
              <p className="sm-card-sub">View, edit and manage stem count configurations</p>
            </div>
          </div>

          <div className="sm-table-wrap">
            <table className="sm-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Stems Count</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {bunchSizes.length > 0 ? (
                  bunchSizes.map((item, i) => (
                    <tr key={item.id}>
                      <td><span className="sm-row-index">{i + 1}</span></td>
                      <td>
                        <span className="sm-value-badge">
                          {item.stems_count} stems
                        </span>
                      </td>
                      <td>
                        <div className="sm-table-actions">
                          <button className="sm-btn sm-btn--edit" onClick={() => handleEdit(item)}>Edit</button>
                          <button className="sm-btn sm-btn--danger" onClick={() => handleDelete(item.id)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3}>
                      <div className="sm-empty">
                        <div className="sm-empty-icon">💐</div>
                        <p className="sm-empty-text">No bunch sizes found</p>
                        <p className="sm-empty-sub">Add your first bunch size using the form</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ManageBunchSizes;